import { useState, useEffect } from "react";
import { useAuth, apiClient } from "../App";
import Layout from "../components/Layout";
import { toast } from "sonner";
import { SortableHeader, toggleSort, applySortFn, getPersistedSort } from "../components/SortableHeader";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { 
  Plus, 
  Users,
  Copy,
  Check,
  MoreVertical,
  UserMinus,
  Shield,
  Mail,
  Clock,
  Link as LinkIcon,
  Trash2
} from "lucide-react";

const Team = () => {
  const { user, tenant } = useAuth();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteLink, setInviteLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: "", description: "", onConfirm: null });
  const [sort, setSort] = useState(() => getPersistedSort("team", "name"));

  const [inviteData, setInviteData] = useState({
    email: "",
    role: "employee"
  });

  const isOwner = user?.role === "owner";
  const isManager = user?.role === "manager";
  const canManageTeam = isOwner || isManager;

  useEffect(() => {
    if (canManageTeam) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [canManageTeam]);

  const fetchData = async () => {
    try {
      const [membersRes, invitesRes] = await Promise.all([
        apiClient.get('/team'),
        apiClient.get('/team/invites')
      ]);
      setMembers(membersRes.data.members);
      setInvites(invitesRes.data.invites);
    } catch (error) {
      console.error("Failed to fetch team data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvite = async (prefillEmail, prefillRole) => {
    setSaving(true);
    try {
      const email = prefillEmail || inviteData.email || null;
      const role = prefillRole || inviteData.role;
      const response = await apiClient.post('/team/invite', {
        email,
        role
      });
      
      // Generate the invite link
      const baseUrl = window.location.origin;
      const link = `${baseUrl}/invite/${response.data.invite_token}`;
      setInviteLink({
        link,
        role: response.data.role,
        company: response.data.company_name,
        emailSent: response.data.email_sent || false,
        emailTo: email,
      });

      if (email) {
        if (response.data.email_sent) {
          toast.success(`Invite emailed to ${email}`);
        } else {
          toast.warning("Invite created — but emailing failed. Share the link manually below.");
        }
      }

      fetchData();
    } catch (error) {
      console.error("Failed to create invite:", error);
      toast.error(error.response?.data?.detail || "Failed to create invite");
    } finally {
      setSaving(false);
    }
  };

  const handleResendInvite = async (inviteId, inviteEmail) => {
    try {
      await apiClient.post(`/team/invites/${inviteId}/resend`);
      toast.success(`Invite re-sent to ${inviteEmail}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to resend invite");
    }
  };

  const handleCopyLink = () => {
    if (inviteLink) {
      navigator.clipboard.writeText(inviteLink.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleCancelInvite = async (inviteId) => {
    setConfirmDialog({
      open: true, title: "Cancel Invite?", description: "This will revoke the invite link. You can create a new one later.",
      onConfirm: async () => {
        try { await apiClient.delete(`/team/invites/${inviteId}`); fetchData(); } catch (e) { console.error(e); }
        setConfirmDialog(d => ({ ...d, open: false }));
      }
    });
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await apiClient.put(`/team/${userId}/role`, { role: newRole });
      fetchData();
    } catch (error) {
      console.error("Failed to update role:", error);
      toast.error(error.response?.data?.detail || "Failed to update role");
    }
  };

  const handleRemoveMember = async (userId, memberName) => {
    setConfirmDialog({
      open: true, title: "Remove Team Member?", description: `${memberName} will lose access to this workspace. You can re-invite them later.`,
      onConfirm: async () => {
        try { await apiClient.delete(`/team/${userId}`); fetchData(); } catch (e) { toast.error(e.response?.data?.detail || "Failed to remove member"); }
        setConfirmDialog(d => ({ ...d, open: false }));
      }
    });
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case "owner": return "bg-[#0A192F] hover:bg-[#0A192F]";
      case "manager": return "bg-[#0D9488] hover:bg-[#0D9488]";
      default: return "bg-slate-500 hover:bg-slate-500";
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const dateOnly = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    return new Date(dateOnly + 'T12:00:00').toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (!canManageTeam) {
    return (
      <Layout>
        <div className="text-center py-12">
          <Shield className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-[#0A192F] mb-2">Access Restricted</h2>
          <p className="text-slate-500">Only owners and managers can access team management.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="team-page" className="space-y-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-[#0A192F] font-['Manrope'] tracking-tight">
              Team Management
            </h1>
            <p className="text-slate-600 mt-1">
              Manage team members and roles for {tenant?.company_name}
            </p>
          </div>
          <Dialog open={showInviteDialog} onOpenChange={(open) => {
            setShowInviteDialog(open);
            if (!open) {
              setInviteLink(null);
              setInviteData({ email: "", role: "employee" });
            }
            if (open) {
              setInviteLink(null);
            }
          }}>
            <DialogTrigger asChild>
              <Button 
                className="bg-[#0D9488] hover:bg-[#14B8A6]"
                data-testid="invite-member-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Invite Member
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Invite Team Member</DialogTitle>
                <DialogDescription>
                  Create an invite link to share with a new team member.
                </DialogDescription>
              </DialogHeader>
              
              {!inviteLink ? (
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="email">Email <span className="text-slate-400 font-normal">(recommended)</span></Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="teammate@example.com"
                      value={inviteData.email}
                      onChange={(e) => setInviteData({...inviteData, email: e.target.value})}
                      data-testid="invite-email-input"
                    />
                    <p className="text-xs text-slate-500">
                      We'll email the invite link directly. Leave blank to copy/share manually.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="role">Role</Label>
                    <Select 
                      value={inviteData.role} 
                      onValueChange={(val) => setInviteData({...inviteData, role: val})}
                    >
                      <SelectTrigger data-testid="invite-role-select">
                        <SelectValue placeholder="Select role" />
                      </SelectTrigger>
                      <SelectContent>
                        {isOwner && <SelectItem value="manager">Manager</SelectItem>}
                        <SelectItem value="employee">Employee</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500">
                      {inviteData.role === "manager" 
                        ? "Managers can manage employees, schedules, and payments."
                        : "Employees can submit time entries and availability."}
                    </p>
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button variant="outline" onClick={() => setShowInviteDialog(false)}>
                      Cancel
                    </Button>
                    <Button 
                      onClick={() => handleCreateInvite()}
                      disabled={saving}
                      className="bg-[#0D9488] hover:bg-[#14B8A6]"
                      data-testid="create-invite-btn"
                    >
                      {saving ? "Creating..." : "Create Invite Link"}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4 py-4">
                  <div className="p-4 bg-[#CCFBF1] rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Check className="w-5 h-5 text-[#0D9488]" />
                      <span className="font-semibold text-[#0D9488]">Invite Created!</span>
                    </div>
                    <p className="text-sm text-slate-600">
                      {inviteLink.emailSent
                        ? <>Invite emailed to <span className="font-medium">{inviteLink.emailTo}</span>. The link below is for backup — it works for anyone and expires in 7 days.</>
                        : <>Share this link with the new {inviteData.role}. It expires in 7 days.</>}
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Invite Link</Label>
                    <div className="flex gap-2">
                      <Input
                        value={inviteLink.link}
                        readOnly
                        className="font-mono text-sm"
                        data-testid="invite-link-input"
                      />
                      <Button
                        variant="outline"
                        onClick={handleCopyLink}
                        className="shrink-0"
                        data-testid="copy-link-btn"
                      >
                        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>

                  <div className="flex justify-end pt-4">
                    <Button 
                      onClick={() => {
                        setShowInviteDialog(false);
                        setInviteLink(null);
                        setInviteData({ email: "", role: "employee" });
                      }}
                      className="bg-[#0D9488] hover:bg-[#14B8A6]"
                    >
                      Done
                    </Button>
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>

        {/* Role Legend */}
        <Card className="border border-slate-200">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-6 text-sm">
              <span className="text-slate-500 font-medium">Roles:</span>
              <div className="flex items-center gap-2">
                <Badge className={getRoleBadgeColor("owner")}>Owner</Badge>
                <span className="text-slate-600">Full access, manage team & settings</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={getRoleBadgeColor("manager")}>Manager</Badge>
                <span className="text-slate-600">Manage employees, schedules, payments</span>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={getRoleBadgeColor("employee")}>Employee</Badge>
                <span className="text-slate-600">Submit time & availability</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Team Members */}
        <Card className="border border-slate-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[#0D9488]" />
              Team Members ({members.length})
            </CardTitle>
          </CardHeader>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead><SortableHeader label="Member" sortKey="name" sortState={sort} onSort={(k) => setSort(s => toggleSort(s, k, "team"))} /></TableHead>
                  <TableHead><SortableHeader label="Email" sortKey="email" sortState={sort} onSort={(k) => setSort(s => toggleSort(s, k, "team"))} /></TableHead>
                  <TableHead><SortableHeader label="Role" sortKey="role" sortState={sort} onSort={(k) => setSort(s => toggleSort(s, k, "team"))} /></TableHead>
                  <TableHead><SortableHeader label="Joined" sortKey="created_at" sortState={sort} onSort={(k) => setSort(s => toggleSort(s, k, "team"))} /></TableHead>
                  {canManageTeam && <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  [...Array(3)].map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={5}>
                        <div className="h-12 bg-slate-100 rounded animate-pulse"></div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : members.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12 text-slate-500">
                      No team members yet. Invite someone to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  [...members].sort(applySortFn(sort)).map((member) => (
                    <TableRow key={member.user_id || member.employee_id} data-testid="team-member-row">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="w-10 h-10">
                            <AvatarImage src={member.picture} alt={member.name} />
                            <AvatarFallback className="bg-[#0D9488] text-white">
                              {member.name?.charAt(0) || 'U'}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium text-[#0A192F]">
                              {member.name}
                              {member.user_id === user?.user_id && (
                                <span className="text-slate-400 ml-2">(You)</span>
                              )}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600">{member.email || <span className="text-slate-400 italic">No email</span>}</TableCell>
                      <TableCell>
                        {member.source === "employee_only" ? (
                          <Badge variant="outline" className="text-slate-400 border-slate-300">Not Invited</Badge>
                        ) : (
                          <Badge className={getRoleBadgeColor(member.role)}>
                            {member.role}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {formatDate(member.created_at)}
                      </TableCell>
                      {canManageTeam && (
                        <TableCell className="text-right">
                          {member.source === "employee_only" ? (
                            (() => {
                              const hasPendingInvite = invites.some(inv => inv.email && member.email && inv.email.toLowerCase() === member.email.toLowerCase());
                              return hasPendingInvite ? (
                                <Badge variant="outline" className="text-amber-600 border-amber-300">Invite Pending</Badge>
                              ) : (
                                <Button
                                  size="sm"
                                  className="bg-[#0D9488] hover:bg-[#14B8A6] text-white"
                                  onClick={() => {
                                    setInviteData({ email: member.email || "", role: "employee" });
                                    setShowInviteDialog(true);
                                  }}
                                  data-testid={`invite-employee-${member.employee_id}`}
                                >
                                  <LinkIcon className="w-3 h-3 mr-1" />
                                  Invite
                                </Button>
                              );
                            })()
                          ) : member.user_id !== user?.user_id && isOwner ? (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" data-testid="member-actions-btn">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {member.role !== "owner" && (
                                  <DropdownMenuItem onClick={() => handleUpdateRole(member.user_id, "owner")}>
                                    <Shield className="w-4 h-4 mr-2" />
                                    Make Owner
                                  </DropdownMenuItem>
                                )}
                                {member.role !== "manager" && (
                                  <DropdownMenuItem onClick={() => handleUpdateRole(member.user_id, "manager")}>
                                    <Shield className="w-4 h-4 mr-2" />
                                    Make Manager
                                  </DropdownMenuItem>
                                )}
                                {member.role !== "employee" && (
                                  <DropdownMenuItem onClick={() => handleUpdateRole(member.user_id, "employee")}>
                                    <Users className="w-4 h-4 mr-2" />
                                    Make Employee
                                  </DropdownMenuItem>
                                )}
                                <DropdownMenuItem 
                                  onClick={() => handleRemoveMember(member.user_id, member.name)}
                                  className="text-[#E11D48]"
                                >
                                  <UserMinus className="w-4 h-4 mr-2" />
                                  Remove from Team
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          ) : null}
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>

        {/* Pending Invites */}
        {invites.length > 0 && (
          <Card className="border border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <LinkIcon className="w-5 h-5 text-[#0D9488]" />
                Pending Invites ({invites.length})
              </CardTitle>
            </CardHeader>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 hover:bg-slate-50">
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Email</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Role</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Expires</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invites.map((invite) => (
                    <TableRow key={invite.invite_id} data-testid="invite-row">
                      <TableCell className="text-slate-600">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-slate-400" />
                          {invite.email || <span className="text-slate-400 italic">No email specified</span>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getRoleBadgeColor(invite.role)}>
                          {invite.role}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-600">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-slate-400" />
                          {formatDate(invite.expires_at)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {invite.email && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleResendInvite(invite.invite_id, invite.email)}
                            className="text-[#0D9488] hover:text-[#0D9488] hover:bg-teal-50 mr-1"
                            data-testid="resend-invite-btn"
                            title="Resend invite email"
                          >
                            <Mail className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCancelInvite(invite.invite_id)}
                          className="text-[#E11D48] hover:text-[#E11D48] hover:bg-red-50"
                          data-testid="cancel-invite-btn"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
        )}
      </div>

      {/* Confirm Dialog */}
      <Dialog open={confirmDialog.open} onOpenChange={(open) => !open && setConfirmDialog(d => ({ ...d, open: false }))}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#0A192F]">{confirmDialog.title}</DialogTitle>
            <DialogDescription className="pt-2">{confirmDialog.description}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialog(d => ({ ...d, open: false }))}>Cancel</Button>
            <Button className="bg-[#E11D48] hover:bg-[#BE123C] text-white" onClick={confirmDialog.onConfirm}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default Team;
